import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
	Stack,
	Pagination,
	TextField,
	InputAdornment,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Button,
	Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import Page from '../../components/Page';
import CodeIcon from '@mui/icons-material/Code';
import { toWapi } from '../../util/api';

class GroupInfo extends Component {
	state = {
		groups: [],
		loadingPhones: true,
		loadingData: false,
		error: null,
		currentPage: 1,
		itemsPerPage: 50,
		searchTerm: '',
		phones: [],
		selectedPhoneId: '',
		filterType: 'removed',
	};

	componentDidMount() {
		this.fetchPhones();
	}

	fetchPhones = async () => {
		const { product } = this.props;
		try {
			const path = `/api/${product.id}/listPhones`;
			const response = await toWapi(path, product.settings.wapi_token);

			if (response.data) {
				const activePhones = response.data.filter((phone) => phone.status !== 'qr-screen' && phone.status !== 'idle');

				this.setState({
					phones: activePhones,
					loadingPhones: false,
				});
			}
		} catch (error) {
			console.error('Error fetching phones:', error);
			this.setState({ loadingPhones: false, error: 'Failed to load phones.' });
		}
	};

	fetchGroups = async (phoneId, filterType) => {
		if (!phoneId) return;

		const { product } = this.props;
		this.setState({ loadingData: true, groups: [], error: null });

		try {
			const path = `/api/${product.id}/${phoneId}/getGroups?${filterType}=true`;
			const response = await toWapi(path, product.settings.wapi_token);

			if (response.data && response.data.success) {
				this.setState({
					groups: response.data.data || [],
					loadingData: false,
				});
			} else {
				throw new Error(response.data.message || 'Failed to fetch group information');
			}
		} catch (error) {
			console.error('Error fetching groups:', error);
			this.setState({
				loadingData: false,
				error: error.message || 'An error occurred while fetching data',
				groups: [],
			});
		}
	};

	handlePageChange = (event, value) => {
		this.setState({ currentPage: value });
	};

	handleSearchChange = (event) => {
		this.setState({ searchTerm: event.target.value, currentPage: 1 });
	};

	handlePhoneSelectChange = (event) => {
		const phoneId = event.target.value;
		this.setState({
			selectedPhoneId: phoneId,
			currentPage: 1,
		});

		const { filterType } = this.state;
		this.fetchGroups(phoneId, filterType);
	};

	handleFilterTypeChange = (event) => {
		const newFilterType = event.target.value;
		this.setState({
			filterType: newFilterType,
			currentPage: 1,
		});

		const { selectedPhoneId } = this.state;
		if (selectedPhoneId) {
			this.fetchGroups(selectedPhoneId, newFilterType);
		}
	};

	downloadCSV = () => {
		const { groups } = this.state;
		const filteredData = this.filterData(groups || []);

		if (!filteredData.length) return;

		const headers = ['Group ID', 'Group Name', 'Participant Count', 'Admin Count'];

		const csvData = filteredData.map((group) => [
			group.id,
			group.name,
			group.participants ? group.participants.length : 0,
			group.admins ? group.admins.length : 0,
		]);

		const csvContent = [headers, ...csvData].map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `inactive-groups-${new Date().toISOString().split('T')[0]}.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	filterData = (data) => {
		const { searchTerm } = this.state;
		let filteredData = data;

		if (searchTerm) {
			const lowerSearchTerm = searchTerm.toLowerCase();
			filteredData = filteredData.filter(
				(item) => (item.name && item.name.toLowerCase().includes(lowerSearchTerm)) || (item.id && item.id.includes(lowerSearchTerm))
			);
		}

		return filteredData;
	};

	render() {
		const { groups, loadingPhones, loadingData, error, currentPage, itemsPerPage, searchTerm, phones, selectedPhoneId, filterType } = this.state;

		if (loadingPhones) {
			return (
				<Page title="Developers" subtitle="Group Info" icon={<CodeIcon />}>
					<Stack sx={{ marginTop: '20%', marginLeft: '50%' }}>
						<CircularProgress disableShrink />
					</Stack>
				</Page>
			);
		}

		if (error && !loadingData) {
			return (
				<Page title="Developers" subtitle="Group Info" icon={<CodeIcon />}>
					<Typography variant="h6" color="error" sx={{ textAlign: 'center', mt: 4 }}>
						{error}
					</Typography>
					<Typography variant="body1" sx={{ textAlign: 'center', mt: 1 }}>
						Please try refreshing the page or check your connection.
					</Typography>
				</Page>
			);
		}

		const filteredData = this.filterData(groups || []);
		const indexOfLastItem = currentPage * itemsPerPage;
		const indexOfFirstItem = indexOfLastItem - itemsPerPage;
		const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
		const pageCount = Math.ceil(filteredData.length / itemsPerPage);

		return (
			<Page title="Developers" subtitle="Group Info" icon={<CodeIcon />}>
				<Stack spacing={2}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
							<FormControl sx={{ minWidth: 200, mt: 1 }}>
								<InputLabel>Filter by Phone</InputLabel>
								<Select
									value={selectedPhoneId}
									label="Filter by Phone"
									onChange={this.handlePhoneSelectChange}
									disabled={loadingData}
									MenuProps={{
										PaperProps: {
											style: {
												maxHeight: 48 * 4.5,
												width: 250,
											},
										},
									}}
								>
									{!phones.length && <MenuItem disabled>No phones available.</MenuItem>}
									{phones
										.sort((a, b) => a.id - b.id)
										.map((phone) => (
											<MenuItem key={phone.id} value={phone.id}>
												{phone.id} {phone.number ? `(${phone.number})` : `(${phone.status})`}
											</MenuItem>
										))}
								</Select>
							</FormControl>

							<FormControl sx={{ minWidth: 150, mt: 1 }}>
								<InputLabel>Filter by Type</InputLabel>
								<Select
									value={filterType}
									label="Filter by Type"
									onChange={this.handleFilterTypeChange}
									disabled={loadingData || !selectedPhoneId}
								>
									<MenuItem value="removed">Removed</MenuItem>
									<MenuItem value="suspended">Suspended</MenuItem>
								</Select>
							</FormControl>

                            {selectedPhoneId && !loadingData && (
                                <Box sx={{ mt: 1.5, ml: -1.5 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {filterType === 'removed'
                                            ? 'Lists the groups the user has been removed from'
                                            : 'Lists groups suspended by WhatsApp.'}
                                    </Typography>
                                </Box>
                            )}

							{loadingData && <CircularProgress size={24} sx={{ mt: 2, ml: 1 }} />}
						</Box>

						<Button
							variant="contained"
							startIcon={<DownloadIcon />}
							onClick={this.downloadCSV}
							disabled={!filteredData.length || loadingData}
							sx={{ mt: 1 }}
						>
							Download CSV
						</Button>
					</Box>

					<TextField
						fullWidth
						variant="outlined"
						placeholder="Search by Group Name or Group ID"
						value={searchTerm}
						onChange={this.handleSearchChange}
						sx={{ mb: 2 }}
						disabled={!selectedPhoneId}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>

					<TableContainer component={Paper}>
						<Table sx={{ background: '#fafafa' }}>
							<TableHead>
								<TableRow>
									<TableCell>
										<strong>Group Name</strong>
									</TableCell>
									<TableCell>
										<strong>Group ID</strong>
									</TableCell>
									<TableCell>
										<strong>Participant Count</strong>
									</TableCell>
									<TableCell>
										<strong>Admin Count</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!selectedPhoneId ? (
									<TableRow>
										<TableCell style={{ textAlign: 'center' }} colSpan="4">
											Please select a phone to view groups.
										</TableCell>
									</TableRow>
								) : loadingData ? (
									<TableRow>
										<TableCell style={{ textAlign: 'center' }} colSpan="4">
											<CircularProgress size={30} sx={{ my: 3 }} />
										</TableCell>
									</TableRow>
								) : !currentItems.length ? (
									<TableRow>
										<TableCell style={{ textAlign: 'center' }} colSpan="4">
											No matching results found.
										</TableCell>
									</TableRow>
								) : (
									currentItems.map((group) => (
										<TableRow key={group.id}>
											<TableCell>{group.name || 'N/A'}</TableCell>
											<TableCell>{group.id || 'N/A'}</TableCell>
											<TableCell>{group.participants ? group.participants.length : 0}</TableCell>
											<TableCell>{group.admins ? group.admins.length : 0}</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{pageCount > 1 && (
						<Stack spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
							<Pagination count={pageCount} page={currentPage} onChange={this.handlePageChange} color="primary" showFirstButton showLastButton />
						</Stack>
					)}

					<Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
						Total Records: {filteredData.length}
					</Typography>
				</Stack>
			</Page>
		);
	}
}

export default connect((state) => ({ user: state.access.user.loggedUserObj, product: state.product.maytapi }))(GroupInfo);
